import { Activity, Calculator, CircleDot, Gauge, Percent, Sigma, Variable } from 'lucide-react';

const units = ['1', '%', 'V', 'A', 'W', 'kW', 'MW', 'VAR', 'kVAR', 'Wh', 'kWh', 'MWh', 'VARh', 'kVARh'];
const field = (name, label, type = 'text', extra = {}) => ({ name, label, type, ...extra });

export const OPTION_LABELS = {
  all: 'Todos los datos', '15min': 'Cada 15 minutos', '30min': 'Cada 30 minutos', hour: 'Por hora', day: 'Por día', month: 'Por mes',
  last: 'Último valor', avg: 'Promedio', sum: 'Suma', min: 'Mínimo', max: 'Máximo', delta: 'Diferencia acumulada',
  period_sum: 'Total del período', period_avg: 'Promedio del período', period_max: 'Mayor valor del período', period_min: 'Menor valor del período', period_last: 'Último valor del período',
  null: 'Dejar sin valor', skip: 'Omitir dato', zero: 'Usar cero', fail: 'Detener con error',
  total: 'Total', R: 'Fase R', S: 'Fase S', T: 'Fase T',
};

export const NODE_PRESENTATION = {
  'source.basic_parameter': {
    label: 'Parámetro básico', category: 'Fuentes', icon: Gauge,
    description: 'Obtiene mediciones de un parámetro básico de SIGER.',
    fields: [
      field('unit', 'Unidad', 'select', { options: units }),
      field('phase', 'Fase', 'select', { options: ['total', 'R', 'S', 'T'], optional: true }),
      field('aggregation', 'Cómo resumir las mediciones', 'catalog', { catalog: 'aggregations' }),
      field('frequency', 'Frecuencia', 'catalog', { catalog: 'frequencies' }),
      field('missing_policy', 'Datos faltantes', 'catalog', { catalog: 'missing_policies' }),
    ], ports: { value: 'Valor' },
  },
  'source.legacy_calculated_parameter': {
    label: 'Parámetro calculado existente', category: 'Fuentes', icon: Activity,
    description: 'Utiliza un parámetro calculado existente configurado en SIGER.',
    fields: [
      field('unit', 'Unidad', 'select', { options: units }),
      field('phase', 'Fase', 'select', { options: ['total', 'R', 'S', 'T'], optional: true }),
      field('aggregation', 'Cómo resumir las mediciones', 'catalog', { catalog: 'aggregations' }),
      field('frequency', 'Frecuencia', 'catalog', { catalog: 'frequencies' }),
      field('missing_policy', 'Datos faltantes', 'catalog', { catalog: 'missing_policies' }),
    ], ports: { value: 'Valor' },
  },
  'constant.number': {
    label: 'Constante', category: 'Fuentes', icon: CircleDot,
    description: 'Valor numérico fijo.',
    fields: [field('value', 'Valor', 'number'), field('unit', 'Unidad', 'select', { options: units })], ports: { value: 'Valor' },
  },
  'variable.manual': {
    label: 'Variable manual', category: 'Fuentes', icon: Variable,
    description: 'Valor suministrado al ejecutar el modelo.',
    fields: [field('key', 'Nombre de la variable'), field('unit', 'Unidad', 'select', { options: units }), field('missing_policy', 'Datos faltantes', 'catalog', { catalog: 'missing_policies', optional: true })], ports: { value: 'Valor' },
  },
  'math.add': { label: 'Sumar', category: 'Operaciones', icon: Sigma, description: 'Suma todos los valores conectados.', fields: [], ports: { terms: 'Valores', result: 'Resultado' } },
  'math.subtract': { label: 'Restar', category: 'Operaciones', icon: Calculator, description: 'Resta el segundo valor al primero.', fields: [], ports: { left: 'Valor 1', right: 'Valor 2', result: 'Resultado' } },
  'math.multiply': { label: 'Multiplicar', category: 'Operaciones', icon: Calculator, description: 'Multiplica dos valores.', fields: [], ports: { left: 'Valor 1', right: 'Valor 2', result: 'Resultado' } },
  'math.divide': { label: 'Dividir', category: 'Operaciones', icon: Calculator, description: 'Divide el primer valor por el segundo.', fields: [], ports: { numerator: 'Numerador', denominator: 'Denominador', result: 'Resultado' } },
  'math.percentage': { label: 'Porcentaje', category: 'Operaciones', icon: Percent, description: 'Aplica un porcentaje al valor de entrada.', fields: [field('percentage', 'Porcentaje', 'number')], ports: { value: 'Entrada', result: 'Resultado' } },
  'output.number': { label: 'Salida numérica', category: 'Salida', icon: CircleDot, description: 'Expone un resultado del modelo.', fields: [
    field('key', 'Nombre del resultado'),
    field('unit', 'Unidad', 'select', { options: units }),
    field('period_reducer', '¿Qué representa el resultado del período?', 'select', {
      options: ['sum', 'avg', 'max', 'min', 'last'], optional: true,
      emptyLabel: 'Elegí una opción',
      help: 'Esta opción indica qué valor querés comparar entre el período de referencia y el período evaluado.',
      optionLabelPrefix: 'period_',
    }),
  ], ports: { value: 'Entrada' } },
};

export const categories = ['Fuentes', 'Operaciones', 'Salida'];

export const initialConfigFor = type => {
  if (type === 'source.basic_parameter' || type === 'source.legacy_calculated_parameter') return { timezone: 'America/Argentina/Buenos_Aires' };
  return {};
};
